import { Injectable, inject } from '@angular/core';

import { TimeService } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import type { LocationBrief } from '@core/locations-types';
import type { RequisitionOpening } from '@core/requisitions-types';
import { RequisitionOpeningReason } from '@core/requisitions-types';

@Injectable({ providedIn: 'root' })
export class RequisitionsCommonTextsService {
  private readonly i18n = inject(I18nService);
  private readonly timeService = inject(TimeService);

  private readonly texts = {
    newPosition: this.i18n.translate('app.requisitions.openings.reason.new_position'),
    replacement: this.i18n.translate('app.requisitions.openings.reason.replacement'),
    jobOpenings: (count: number) =>
      this.i18n.translate('app.requisitions.openings.job_openings', {
        count,
      }),
    replacements: (count: number) =>
      this.i18n.translate('app.requisitions.openings.replacements', {
        count,
      }),
    newPositions: (count: number) =>
      this.i18n.translate('app.requisitions.openings.new_positions', {
        count,
      }),
    remote: this.i18n.translate('app.offer_form.remote'),
    archived: this.i18n.translate('app.locations.archived').toLowerCase(),
  };

  public getFormattedAnticipatedStartDate(value: string | null): string {
    if (!value) return '';
    return this.timeService.format({ input: value, pattern: 'DD MMM YYYY' });
  }

  public getOpeningReason(value: string | null): string {
    if (value === RequisitionOpeningReason.NewPosition) return this.texts.newPosition;
    if (value === RequisitionOpeningReason.Replacement) return this.texts.replacement;
    return '';
  }

  public getJobOpeningsText(openings: RequisitionOpening[]): string {
    const hasReasons = openings.every(opening => !!opening.reason);

    if (!hasReasons) {
      return this.texts.jobOpenings(openings.length);
    }

    return this.getReasonForOpeningsText(openings);
  }

  public getReasonForOpeningsText(openings: RequisitionOpening[]): string {
    const newPositionsCount = openings.filter(
      opening => opening.reason === RequisitionOpeningReason.NewPosition,
    ).length;
    const replacementsCount = openings.filter(
      opening => opening.reason === RequisitionOpeningReason.Replacement,
    ).length;

    if (newPositionsCount && replacementsCount) {
      return `${this.texts.replacements(replacementsCount)}, ${this.texts.newPositions(
        newPositionsCount,
      )}`;
    }

    if (replacementsCount) {
      return this.texts.replacements(replacementsCount);
    }

    if (newPositionsCount) {
      return this.texts.newPositions(newPositionsCount);
    }

    return '';
  }

  public getLocationText(item: { locations?: LocationBrief[] }): string {
    return (
      item.locations
        ?.map(location =>
          location.archivedAt ? `${location.name} (${this.texts.archived})` : location.name,
        )
        .join(', ') || ''
    );
  }
}
